import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { IconButton, InputAdornment, TextField, TextFieldProps, TextFieldVariants } from '@mui/material';
import React from 'react';

type Props<Variant extends TextFieldVariants> = Omit<TextFieldProps<Variant>, 'onChange'> & {
  onChange(textSearch: string): void;
  closeSearchMode?: () => void;
  clearAriaLabel?: string;
  showSearchIcon?: boolean;
};

const SearchBar = <Variant extends TextFieldVariants>({
  helperText,
  onChange,
  closeSearchMode,
  clearAriaLabel = 'Clear search',
  showSearchIcon = true,
  InputProps,
  onKeyDown,
  placeholder = 'Search',
  sx,
  type = 'search',
  value,
  autoFocus = Boolean(closeSearchMode),
  ...props
}: React.PropsWithChildren<Props<Variant>>) => {
  const hasSearchValue = value !== undefined && value !== null && String(value).trim() !== '';
  const shouldShowClear = hasSearchValue || Boolean(closeSearchMode);

  const handleClear = () => {
    onChange('');
    closeSearchMode?.();
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    onKeyDown?.(event);

    if (!event.defaultPrevented && event.key === 'Escape' && hasSearchValue) {
      event.preventDefault();
      onChange('');
    }
  };

  return (
    <TextField
      {...props}
      type={type}
      value={value}
      placeholder={placeholder}
      helperText={helperText}
      autoFocus={autoFocus}
      sx={[
        {
          '& input[type="search"]::-webkit-search-cancel-button': {
            WebkitAppearance: 'none',
            appearance: 'none',
            display: 'none',
          },
          '& input[type="search"]::-webkit-search-decoration': {
            WebkitAppearance: 'none',
            appearance: 'none',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(event.target.value)}
      onKeyDown={handleKeyDown}
      InputProps={{
        ...InputProps,
        startAdornment:
          showSearchIcon && !InputProps?.startAdornment ? (
            <InputAdornment position="start">
              <SearchRoundedIcon fontSize="small" />
            </InputAdornment>
          ) : (
            InputProps?.startAdornment
          ),
        endAdornment: (
          <>
            {shouldShowClear ? (
              <InputAdornment position="end">
                <IconButton aria-label={clearAriaLabel} onClick={handleClear} edge="end" size="small" sx={{ mx: 0 }}>
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null}
            {InputProps?.endAdornment}
          </>
        ),
      }}
    />
  );
};

export default SearchBar;
